import { supabase } from '../lib/supabase'

export type StatoRegistro = 'in_coda' | 'pronto' | 'errore'

export interface Registro {
  readonly id: number
  readonly impiantoId: number
  readonly anno: number
  readonly stato: StatoRegistro
  readonly errore: string | null
  readonly creatoIl: string
  readonly impianto: string
  readonly origine: 'generato' | 'caricato'
  /** Solo per i caricati: il nome con cui il partner l'ha caricato. */
  readonly nomeFile: string | null
  /** Dove partirebbe la richiesta di vidimazione: null se l'azienda non ce l'ha. */
  readonly emailCliente: string | null
  readonly ultimoInvio: UltimoInvio | null
}

export interface UltimoInvio {
  readonly stato: 'in_coda' | 'inviata' | 'errore'
  readonly errore: string | null
  readonly creatoIl: string
}

type Annidato<T> = T | T[] | null
const uno = <T,>(v: Annidato<T>): T | null => (Array.isArray(v) ? (v[0] ?? null) : v)

interface RigaRegistro {
  id: number
  impianto_id: number
  anno: number
  stato: StatoRegistro
  errore: string | null
  creato_il: string
  origine: Registro['origine']
  nome_file: string | null
  impianto: Annidato<{ codice_ditta: string; azienda: Annidato<{ ragione_sociale: string; email: string | null }> }>
  notifica_inviata: { stato: UltimoInvio['stato']; errore: string | null; creato_il: string }[] | null
}

export async function elencaRegistri(): Promise<Registro[]> {
  const { data, error } = await supabase
    .from('registro')
    .select(
      'id, impianto_id, anno, stato, errore, creato_il, origine, nome_file, impianto(codice_ditta, azienda(ragione_sociale, email)), notifica_inviata(stato, errore, creato_il)',
    )
    .order('creato_il', { ascending: false })
    // Del registro interessa solo l'ultimo invio al cliente (0044).
    .order('creato_il', { referencedTable: 'notifica_inviata', ascending: false })
    .limit(1, { referencedTable: 'notifica_inviata' })
  if (error) throw new Error(error.message)
  return ((data ?? []) as unknown as RigaRegistro[]).map((r) => {
    const impianto = uno(r.impianto)
    const azienda = uno(impianto?.azienda ?? null)
    const invio = r.notifica_inviata?.[0]
    return {
      id: r.id,
      impiantoId: r.impianto_id,
      anno: r.anno,
      stato: r.stato,
      errore: r.errore,
      creatoIl: r.creato_il,
      impianto: [azienda?.ragione_sociale, impianto && `(${impianto.codice_ditta})`].filter(Boolean).join(' '),
      origine: r.origine,
      nomeFile: r.nome_file,
      emailCliente: azienda?.email ?? null,
      ultimoInvio: invio ? { stato: invio.stato, errore: invio.errore, creatoIl: invio.creato_il } : null,
    }
  })
}

/** Il registro lo compone il worker: qui si accoda soltanto (0038). */
export async function chiediRegistro(impiantoId: number, anno: number): Promise<number> {
  const { data, error } = await supabase.rpc('accoda_registro', { p_impianto_id: impiantoId, p_anno: anno })
  if (error) throw new Error(error.message)
  return data as number
}

/** URL firmato a scadenza breve: le credenziali R2 stanno solo sul server. */
export async function scaricaRegistro(registroId: number): Promise<string> {
  const { data, error } = await supabase.functions.invoke('scarica-registro', { body: { registroId } })
  if (error) throw new Error(error.message)
  return (data as { url: string }).url
}

/** Un registro che il partner ha gia' (REG-08): il file va su R2 e la riga la
 * crea l'Edge Function, perche' il percorso del file non deve deciderlo il
 * browser (0042_registro_caricato.sql). */
export async function caricaRegistro(impiantoId: number, anno: number, file: File): Promise<number> {
  const corpo = new FormData()
  corpo.append('impiantoId', String(impiantoId))
  corpo.append('anno', String(anno))
  corpo.append('file', file)
  const { data, error } = await supabase.functions.invoke('carica-registro', { body: corpo })
  if (error) throw new Error(error.message)
  return (data as { registroId: number }).registroId
}

/** Richiesta di vidimazione (REG-09): il registro parte in allegato
 * all'email dell'azienda col modello `richiesta_vidimazione` (0044). */
export async function inviaAlCliente(registroId: number): Promise<number> {
  const { data, error } = await supabase.rpc('invia_registro_vidimazione', { p_registro_id: registroId })
  if (error) throw new Error(error.message)
  return data as number
}
